import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, PlusIcon, ShoppingBagIcon } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { formatPrice } from "../utils/currency";
import type { Product } from "../types/product";
interface ProductCardProps {
  product: Product;
}
const badgeStyles = {
  Bestseller: "bg-brand text-night",
  Hot: "bg-ember text-white",
  New: "bg-night text-white"
};
export function ProductCard({
  product
}: ProductCardProps) {
  const {
    addItem
  } = useCart();
  const [added, setAdded] = useState(false);
  const href = `/product/${product.slug}`;
  const [primary, secondary] = product.images;
  useEffect(() => {
    if (!added) return;
    const t = setTimeout(() => setAdded(false), 1400);
    return () => clearTimeout(t);
  }, [added]);
  const add = () => {
    addItem(product.slug);
    setAdded(true);
  };
  return <article className="group flex flex-col" data-id="element-358" data-mp="304iw0pxu2kjg">
      <div className="relative overflow-hidden rounded-[22px] bg-white ring-1 ring-white/5 transition-shadow duration-300 group-hover:shadow-[0_20px_60px_-20px_rgba(255,212,0,0.35)]" data-id="element-359" data-mp="3lqu8if79sc6d">
        <Link to={href} className="relative block aspect-square" aria-label={product.name} data-id="element-360" data-mp="3gyatrewujazz">
          <img src={primary} alt={product.name} loading="lazy" className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-300 ease-brand group-hover:scale-[1.06]" data-id="element-361" data-mp="3cwfvkja7pdqf" />
          {secondary && <img src={secondary} alt="" loading="lazy" className="absolute inset-0 h-full w-full bg-white object-contain p-2 opacity-0 transition-opacity duration-300 ease-brand group-hover:opacity-100" data-id="element-362" data-mp="4j8n056oojs4" />}
        </Link>
        {product.badge && <span className={`pointer-events-none absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold ${badgeStyles[product.badge]}`} data-id="element-363" data-mp="2k7iegjhqlmz9">
            {product.badge}
          </span>}
        <button type="button" onClick={add} className="absolute inset-x-3 bottom-3 hidden translate-y-16 items-center justify-center gap-2 rounded-full bg-night py-3 text-sm font-bold text-white opacity-0 transition-[transform,opacity,background-color,color] duration-200 ease-brand hover:bg-brand hover:text-night focus-visible:translate-y-0 focus-visible:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 lg:flex" aria-label={`Quick add ${product.name} to cart`} data-id="element-364" data-mp="2lrjy25nuh6fc">
          {added ? <CheckIcon className="h-4 w-4" data-id="element-365" data-mp="1p2ccixn86xbf" /> : <ShoppingBagIcon className="h-4 w-4" data-id="element-366" data-mp="1oyyc3uy4gy6m" />}
          {added ? "Added" : "Quick add"}
        </button>
      </div>

      <div className="mt-4 flex flex-1 flex-col" data-id="element-367" data-mp="1f21y59pgfjuo">
        <p className="text-xs text-subtle" data-id="element-368" data-mp="1f3dd85ocoe13">
          {product.brand} · {product.format}
        </p>
        <h3 className="mt-1 font-display text-base font-semibold leading-snug sm:text-lg" data-id="element-369" data-mp="1k0wpj01hmgqe">
          <Link to={href} className="transition-colors hover:text-brand" data-id="element-370" data-mp="10gem6btenlmw">
            {product.name}
          </Link>
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted" data-id="element-371" data-mp="vj61o4ksb5ih">{product.tagline}</p>
        <div className="mt-auto flex items-center justify-between gap-3 pt-4" data-id="element-372" data-mp="1abzk9yvcs1rd">
          <span className="font-display text-lg font-bold" data-id="element-373" data-mp="15dej9x8a1e1v">{formatPrice(product.price)}</span>
          <button type="button" onClick={add} className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-150 active:scale-[0.94] lg:hidden ${added ? "bg-brand text-night" : "border border-line hover:bg-brand hover:text-night"}`} aria-label={`Add ${product.name} to cart`} data-id="element-374" data-mp="1jdfti3aflxu0">
            {added ? <CheckIcon className="h-4 w-4" data-id="element-375" data-mp="225ruompcmt2k" /> : <PlusIcon className="h-4 w-4" data-id="element-376" data-mp="222w11akjaqjx" />}
          </button>
        </div>
      </div>
    </article>;
}